import {
  DefaultKeyStoreModel,
  KeyStoreModel,
  IKeyStoreData,
  KeyModelVersion,
} from "../../store/key.store.model";
import { IModelView, VaultModelView } from "../model.view";

/**
 * Key View interface for API key data
 */
export interface IKeyView {
  /** API key value */
  apiKey: string;
  /** Login Credentials */
  username?: string;
  password?: string;
}

/**
 * Public Key View interface
 *
 * Represents a key with associated metadata but without exposing vault internals
 */
export type KeyView = IKeyView & IModelView<KeyModelVersion, IKeyStoreData>;

/**
 * Creates a KeyView instance from a store model
 *
 * @param model - Key store model to convert
 * @returns A KeyView instance
 */
export const fromStoreModel = (model: KeyStoreModel): KeyView => {
  return new KeyVaultView(model);
};

/**
 * Creates a KeyView instance from key data
 *
 * @param view - Key data to convert to a view model, clones if already a KeyView, or creates new if plain object
 * @returns A KeyView instance with vaultId hidden from public access
 */
export const fromView = (view: IKeyView | KeyView): KeyView => {
  if (isKeyVaultView(view)) {
    return fromStoreModel(view.toStoreModel());
  }

  const data: KeyStoreModel = {
    id: crypto.randomUUID(),
    dc: new Date().toISOString(),
    dm: new Date().toISOString(),
    v: DefaultKeyStoreModel.v,
    vid: null,
    ak: view.apiKey,
  };
  if (view.username) {
    data.unm = view.username;
  }
  if (view.password) {
    data.pwd = view.password;
  }
  return fromStoreModel(data);
};

/**
 * Type guard to check if a view is already a KeyVaultView
 */
const isKeyVaultView = (view: IKeyView | KeyView): view is KeyVaultView => {
  return typeof (view as KeyVaultView).toStoreModel === "function";
};

/**
 * Key Vault View
 *
 * Internal implementation class for KeyView.
 * Do not expose this class publicly or use directly outside of fromView methods.
 * The vaultId property is intentionally protected to prevent external access.
 */
class KeyVaultView extends VaultModelView<KeyModelVersion, IKeyStoreData> {
  readonly apiKey: string;
  readonly id: string;
  readonly password?: string;
  readonly username?: string;

  constructor(model: KeyStoreModel) {
    super(model);
    this.apiKey = model.ak;
    if (model.unm) {
      this.username = model.unm;
    }
    if (model.pwd) {
      this.password = model.pwd;
    }
  }

  protected toDataModel(): IKeyStoreData {
    const model: IKeyStoreData = {
      ak: this.apiKey,
    };

    if (this.username) {
      model.unm = this.username;
    }

    if (this.password) {
      model.pwd = this.password;
    }

    return model;
  }
}
